// Connection config is entered by the user (this is a general web client) and
// persisted redundantly so a storage-wiping browser can't lose it:
//   1) localStorage (works on normal browsers)
//   2) the URL hash  (survives even if storage is wiped — bookmark carries it)
//
// A RustDesk ID/relay server on host:BASE serves WebSocket on BASE+2 (rendezvous)
// and BASE+3 (relay). Default base is 21116.

export interface RustDeskConfig {
  idServer: string;
  relayServer: string;
  key: string;
  basePort: number;
  schema: "wss://" | "ws://";
}

/** Raw form values as the user types them (server may include :port). */
export interface ConfigForm {
  server: string; // "host" or "host:port"
  relay: string; // optional, "host" or "host:port"
  key: string; // base64, optional
  schema: "wss://" | "ws://";
}

declare global {
  interface Window {
    RUSTDESK_CONFIG?: Partial<ConfigForm>;
  }
}

const LS_KEY = "rustdesk-config";

export const EMPTY_FORM: ConfigForm = { server: "", relay: "", key: "", schema: "wss://" };

/** RustDesk's public-server sign key; fallback only when no custom key is set. */
export const RS_PK = "OeVuKk5nlHiXp+APNn0Y3pC1Iwpwn44JGqrQCsWqmBw=";

function splitHostPort(s: string, defBase: number): { host: string; base: number } {
  const t = s.trim();
  if (t.includes(":")) {
    const [host, portStr] = t.split(":");
    const port = parseInt(portStr, 10);
    return { host, base: Number.isFinite(port) ? port : defBase };
  }
  return { host: t, base: defBase };
}

/** Build the concrete connection config from raw form values. */
export function buildConfig(form: ConfigForm): RustDeskConfig {
  const { host, base } = splitHostPort(form.server, 21116);
  const relayHost = form.relay ? splitHostPort(form.relay, base).host : "";
  return {
    idServer: host,
    relayServer: relayHost,
    key: form.key.trim(),
    basePort: base,
    schema: form.schema,
  };
}

// ---- persistence ------------------------------------------------------------

function fromHash(): Partial<ConfigForm> | undefined {
  const h = location.hash.replace(/^#/, "");
  if (!h) return undefined;
  const p = new URLSearchParams(h);
  if (!p.has("server") && !p.has("key")) return undefined;
  return {
    server: p.get("server") ?? "",
    relay: p.get("relay") ?? "",
    key: p.get("key") ?? "",
    schema: (p.get("schema") as ConfigForm["schema"]) ?? "wss://",
  };
}

function fromLocalStorage(): Partial<ConfigForm> | undefined {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Partial<ConfigForm>) : undefined;
  } catch {
    return undefined;
  }
}

/** Prefill order: URL hash > localStorage > window.RUSTDESK_CONFIG > empty. */
export function loadForm(): ConfigForm {
  return {
    ...EMPTY_FORM,
    ...(typeof window !== "undefined" ? window.RUSTDESK_CONFIG : undefined),
    ...fromLocalStorage(),
    ...fromHash(),
  };
}

/** Persist to localStorage (best effort) and the URL hash (storage-independent). */
export function saveForm(form: ConfigForm, alsoRemoteId?: string) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(form));
  } catch {
    /* storage may be blocked/wiped — the hash below still carries it */
  }
  const p = new URLSearchParams();
  if (form.server) p.set("server", form.server);
  if (form.relay) p.set("relay", form.relay);
  if (form.key) p.set("key", form.key);
  if (form.schema && form.schema !== "wss://") p.set("schema", form.schema);
  if (alsoRemoteId) p.set("id", alsoRemoteId);
  const hash = p.toString();
  if (hash) history.replaceState(null, "", "#" + hash);
}

export function remoteIdFromHash(): string {
  const p = new URLSearchParams(location.hash.replace(/^#/, ""));
  return p.get("id") ?? "";
}

export function rendezvousUri(cfg: RustDeskConfig): string {
  return `${cfg.schema}${cfg.idServer}:${cfg.basePort + 2}`;
}

export function relayUri(cfg: RustDeskConfig): string {
  const host = cfg.relayServer || cfg.idServer;
  return `${cfg.schema}${host}:${cfg.basePort + 3}`;
}

/** Given a relay host advertised by hbbs (may include :port), build its ws uri. */
export function relayUriFromAdvertised(advertised: string, cfg: RustDeskConfig): string {
  if (!advertised) return relayUri(cfg);
  if (advertised.includes(":")) {
    const [host, portStr] = advertised.split(":");
    const port = parseInt(portStr, 10);
    return `${cfg.schema}${host}:${port + 3}`;
  }
  return `${cfg.schema}${advertised}:${cfg.basePort + 3}`;
}
